import { FormEvent, useCallback, useEffect, useState } from 'react';

import { request } from '../lib/api';

type PolicyRule = {
  id?: string;
  matchType: 'email' | 'domain';
  value: string;
  effect: 'allow' | 'deny';
  expiresAt: string | null;
};

type ProtectedResourcePolicy = {
  id: string;
  fileName: string;
  policyVersion: number;
  resolutionVersion: number;
  defaultOutcome: 'owner_review' | 'deny';
  rules: PolicyRule[];
};

type PolicyResponse = { resources: ProtectedResourcePolicy[]; nextCursor: string | null };
type ApiFailure = { status?: number; data?: { message?: string } };

const newRule = (): PolicyRule => ({
  matchType: 'email',
  value: '',
  effect: 'allow',
  expiresAt: null
});

const toLocalDateTimeInput = (value: string | null) => {
  if (!value) return '';
  const date = new Date(value);
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
};

export function ProtectedResourcePolicyControls() {
  const [resources, setResources] = useState<ProtectedResourcePolicy[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [available, setAvailable] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const load = useCallback(async (cursor?: string | null) => {
    if (!cursor) setLoading(true);
    try {
      const response = await request<PolicyResponse>(
        `/me/protected-resources/policies?take=20${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`
      );
      setResources((current) => cursor ? [...current, ...response.resources] : response.resources);
      setNextCursor(response.nextCursor);
      setAvailable(true);
    } catch (error) {
      const failure = error as ApiFailure;
      if (failure.status === 403) setAvailable(false);
      else setMessage(failure.data?.message ?? 'Protected file policies could not be loaded.');
    } finally {
      if (!cursor) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const updateResource = useCallback((resourceId: string, update: (resource: ProtectedResourcePolicy) => ProtectedResourcePolicy) => {
    setResources((current) => current.map((resource) => resource.id === resourceId ? update(resource) : resource));
  }, []);

  const savePolicy = useCallback(async (event: FormEvent<HTMLFormElement>, resource: ProtectedResourcePolicy) => {
    event.preventDefault();
    setBusyId(resource.id);
    setMessage(null);
    try {
      await request(`/me/protected-resources/policies/${encodeURIComponent(resource.id)}`, {
        method: 'PUT',
        body: JSON.stringify({
          defaultOutcome: resource.defaultOutcome,
          rules: resource.rules.map((rule) => ({
            matchType: rule.matchType,
            value: rule.value,
            effect: rule.effect,
            expiresAt: rule.expiresAt || null
          }))
        })
      });
      setMessage(`Access policy saved for ${resource.fileName}. Existing requests and unused permissions were invalidated.`);
      await load(null);
    } catch (error) {
      const failure = error as ApiFailure;
      setMessage(failure.data?.message ?? 'The protected file policy could not be saved.');
    } finally {
      setBusyId(null);
    }
  }, [load]);

  if (!available || (!loading && resources.length === 0)) return null;

  return (
    <section className="protected-policy" aria-labelledby="protected-policy-title">
      <header>
        <div>
          <span className="portal-eyebrow">Access rules</span>
          <h3 id="protected-policy-title">Trusted and blocked requesters</h3>
          <p>Allow rules approve a verified email automatically. Deny rules always win. Changes immediately invalidate outstanding access.</p>
        </div>
      </header>
      {message ? <div className="alert" role="status">{message}</div> : null}
      {loading ? <p className="helper-text">Loading access rules…</p> : null}
      {!loading ? resources.map((resource) => (
        <form className="protected-policy__resource" key={`${resource.id}-${resource.policyVersion}`} onSubmit={(event) => void savePolicy(event, resource)}>
          <div className="protected-policy__resource-heading">
            <div>
              <strong>{resource.fileName}</strong>
              <small>Policy version {resource.policyVersion}</small>
            </div>
            <label>
              Everyone else
              <select
                value={resource.defaultOutcome}
                onChange={(event) => updateResource(resource.id, (current) => ({
                  ...current,
                  defaultOutcome: event.target.value as ProtectedResourcePolicy['defaultOutcome']
                }))}
              >
                <option value="owner_review">Ask me to review</option>
                <option value="deny">Deny automatically</option>
              </select>
            </label>
          </div>

          <div className="protected-policy__rules">
            {resource.rules.map((rule, index) => (
              <div className="protected-policy__rule" key={rule.id ?? `${resource.id}-new-${index}`}>
                <label>
                  Match
                  <select value={rule.matchType} onChange={(event) => updateResource(resource.id, (current) => ({
                    ...current,
                    rules: current.rules.map((item, itemIndex) => itemIndex === index
                      ? { ...item, matchType: event.target.value as PolicyRule['matchType'] }
                      : item)
                  }))}>
                    <option value="email">Exact email</option>
                    <option value="domain">Email domain</option>
                  </select>
                </label>
                <label className="protected-policy__value">
                  {rule.matchType === 'email' ? 'Email' : 'Domain'}
                  <input
                    type={rule.matchType === 'email' ? 'email' : 'text'}
                    required
                    maxLength={254}
                    value={rule.value}
                    placeholder={rule.matchType === 'email' ? 'person@example.com' : 'example.com'}
                    onChange={(event) => updateResource(resource.id, (current) => ({
                      ...current,
                      rules: current.rules.map((item, itemIndex) => itemIndex === index
                        ? { ...item, value: event.target.value }
                        : item)
                    }))}
                  />
                </label>
                <label>
                  Result
                  <select value={rule.effect} onChange={(event) => updateResource(resource.id, (current) => ({
                    ...current,
                    rules: current.rules.map((item, itemIndex) => itemIndex === index
                      ? { ...item, effect: event.target.value as PolicyRule['effect'] }
                      : item)
                  }))}>
                    <option value="allow">Trust after verification</option>
                    <option value="deny">Always deny</option>
                  </select>
                </label>
                <label>
                  Expires
                  <input
                    type="datetime-local"
                    value={toLocalDateTimeInput(rule.expiresAt)}
                    onChange={(event) => updateResource(resource.id, (current) => ({
                      ...current,
                      rules: current.rules.map((item, itemIndex) => itemIndex === index
                        ? { ...item, expiresAt: event.target.value ? new Date(event.target.value).toISOString() : null }
                        : item)
                    }))}
                  />
                </label>
                <button type="button" className="button-secondary" onClick={() => updateResource(resource.id, (current) => ({
                  ...current,
                  rules: current.rules.filter((_item, itemIndex) => itemIndex !== index)
                }))}>Remove</button>
              </div>
            ))}
          </div>

          <div className="protected-policy__actions">
            <button type="button" className="button-secondary" disabled={resource.rules.length >= 100} onClick={() => updateResource(resource.id, (current) => ({
              ...current,
              rules: [...current.rules, newRule()]
            }))}>Add rule</button>
            <button type="submit" className="btn-primary" disabled={busyId === resource.id}>
              {busyId === resource.id ? 'Saving…' : 'Save access policy'}
            </button>
          </div>
        </form>
      )) : null}
      {!loading && nextCursor ? (
        <button
          type="button"
          className="button-secondary protected-policy__load-more"
          disabled={busyId === 'load-more'}
          onClick={() => {
            setBusyId('load-more');
            void load(nextCursor).finally(() => setBusyId(null));
          }}
        >
          {busyId === 'load-more' ? 'Loading…' : 'Load more protected files'}
        </button>
      ) : null}
    </section>
  );
}

export type { PolicyResponse, ProtectedResourcePolicy };
